import { Button, ButtonElement } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import {
  type ReactNode,
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import googleLogo from './assets/google.svg'
import { ALLOWED_CALLBACK_ORIGINS, CANONICAL_SUPPORT_ORIGIN } from './constants'
import styles from './social-sign-in-button.module.scss'

const mapProviderLogo = { google: googleLogo }

function bindIframeLoadBeforeSettingSrc(
  iframe: HTMLIFrameElement,
  src: string,
  getOnLoad: () => void
): () => void {
  const onLoad = () => getOnLoad()
  iframe.addEventListener('load', onLoad)
  iframe.src = src
  return () => iframe.removeEventListener('load', onLoad)
}

/**
 * Props for the Google sign-in button that loads Bifrost's iframe and handles the OAuth callback.
 *
 * @see README in this package for details.
 */
export interface SocialSignInButtonProps {
  children: ReactNode
  featureCode: string
  fromComponent: string
  nextUrl: string
  provider: 'google'
  /**
   * Optional signup data (CPF, phone, etc.) for flows that collect it before Google sign-in.
   * Omit for login/cadastro without extra profile data (e.g. auth app).
   */
  signupData?: {
    birthDate: string
    cpf: string
    fullName: string
    whatsApp: string
  }
  appId: string
  /**
   * Optional opt-out signal forwarded as a `scope` query param on the OAuth-callback URL
   * (`/cadastro/google/${token}?...&scope=...`). Bifrost's google handler spreads
   * `req.query` into the post-signup redirect, so the signal lands on `/cadastro/cpf`
   * where consumer-side gates (e.g. dedicated phone-step experiment) can read it.
   * Not OAuth `scope` — that field is server-side only.
   */
  scope?: string
  onTokenReceived?: () => void
  onError?: (error: unknown) => void
  /** Called when the iframe has loaded so the app can e.g. enable the button or hide loading. */
  onIframeLoad?: () => void
  /**
   * Minimum height (px) for the button container so the iframe content scales to a proper clickable area.
   * Omit when the container already has stable dimensions (e.g. inside a fixed-size modal).
   */
  minHeight?: number
  /** Button size from the design system. Default `md`.. */
  size?: 'sm' | 'md' | 'lg'
  /** Button visual kind from the design system. Default `tertiary`. */
  kind?: 'primary' | 'tertiary'
}

/**
 * Google sign-in button that loads Bifrost's iframe and redirects to Bifrost on success.
 */
export const SocialSignInButton = forwardRef(function SocialSignInButton(
  props: SocialSignInButtonProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    children,
    provider,
    nextUrl,
    featureCode,
    fromComponent,
    signupData,
    appId,
    scope,
    onTokenReceived,
    onError,
    onIframeLoad,
    minHeight,
    size = 'md',
    kind = 'tertiary',
  } = props

  const refIframe = useRef<HTMLIFrameElement | null>(null)
  const refButton = useRef<ButtonElement | null>(null)
  const refCurrentLoadHandler = useRef<(() => void) | undefined>(undefined)

  const handleIframeLoad = useCallback(() => {
    onIframeLoad?.()
    refIframe.current?.contentWindow?.postMessage(
      { socialIframeSupport: true },
      CANONICAL_SUPPORT_ORIGIN
    )
  }, [onIframeLoad])

  refCurrentLoadHandler.current = handleIframeLoad

  const iframeSupportSrc = `${CANONICAL_SUPPORT_ORIGIN}/bifrost/api/signup/iframe-support/${provider}`

  useLayoutEffect(() => {
    const el = refIframe.current
    if (!el) return
    return bindIframeLoadBeforeSettingSrc(el, iframeSupportSrc, () =>
      refCurrentLoadHandler.current?.()
    )
  }, [iframeSupportSrc])

  const onMessage = useCallback(
    async (event: MessageEvent) => {
      const { data, origin } = event

      if (typeof window !== 'undefined') {
        const allowed = [window.location.origin, ...ALLOWED_CALLBACK_ORIGINS]
        if (!allowed.includes(origin)) {
          return console.warn(`[${provider}][callback] origin mismatch`)
        }
      }
      if (!data) return console.warn(`[${provider}][callback] no data`)
      if (!data.token) {
        if (data[provider]) onError?.(new Error(`[${provider}][callback] no token`))
        return console.warn(`[${provider}][callback] no token`)
      }
      if (!data[provider]) return console.warn(`[${provider}][callback] no provider`)

      onTokenReceived?.()

      try {
        const { token } = data
        const callbackPath = `/cadastro/google/${token}`
        const url = new URL(callbackPath, window.location.origin)
        url.searchParams.set('app_id', appId)
        url.searchParams.set('feature_code', featureCode)
        url.searchParams.set('from_component', fromComponent)
        if (nextUrl) {
          url.searchParams.set('next_url', nextUrl)
        }
        if (scope) {
          url.searchParams.set('scope', scope)
        }
        Object.entries(signupData ?? {}).forEach(([key, value]) => {
          url.searchParams.set(key, value)
        })
        const targetWindow = window.top ?? window
        targetWindow.location.assign(url.toString())
      } catch (error) {
        console.error(error)
        onError?.(error)
      }
    },
    [
      appId,
      featureCode,
      fromComponent,
      nextUrl,
      scope,
      provider,
      signupData,
      onTokenReceived,
      onError,
    ]
  )

  useEffect(() => {
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [onMessage])

  return (
    <div ref={ref} className={styles.root} style={{ minHeight: minHeight ?? undefined }}>
      <iframe
        ref={refIframe}
        className={styles.iframeSupport}
        title={typeof children === 'string' ? children : `${provider} social login`}
      />
      <Button
        ref={refButton}
        aria-hidden
        block
        kind={kind}
        size={size}
        tabIndex={-1}
        leftIcon={
          <Image
            className={kind === 'primary' ? `${styles.logo} ${styles.logoBadge}` : styles.logo}
            alt={`${provider} logo`}
            height={20}
            src={mapProviderLogo[provider]}
            width={20}
            unoptimized
          />
        }
      >
        {children}
      </Button>
    </div>
  )
})
